import { Participant } from '../types';

/**
 * Wallet management utilities
 * Handles initial stake, winnings balance, and betting priority logic
 */

/**
 * Calculate total available balance
 * Total available = initialStakeRemaining + winningsBalance
 */
export function calculateTotalAvailable(
  initialStakeRemaining: number,
  winningsBalance: number
): number {
  return initialStakeRemaining + winningsBalance;
}

/**
 * Deduct bet amount from participant wallet
 * Priority: Initial stake first, then winnings
 * 
 * @param participant - Participant to deduct from
 * @param amount - Amount to deduct
 * @returns Updated participant with new balances, or null if insufficient funds
 */
export function deductBetAmount(
  participant: Participant,
  amount: number
): Participant | null {
  // Check if sufficient funds
  const totalAvailable = calculateTotalAvailable(
    participant.initialStakeRemaining,
    participant.winningsBalance
  );

  if (amount > totalAvailable) {
    return null; // Insufficient funds
  }

  let initialStakeRemaining = participant.initialStakeRemaining;
  let winningsBalance = participant.winningsBalance;

  // Deduct from initial stake first
  if (initialStakeRemaining >= amount) {
    initialStakeRemaining -= amount;
  } else {
    // Use remaining initial stake, then deduct from winnings
    const remainingFromInitial = initialStakeRemaining;
    const fromWinnings = amount - remainingFromInitial;
    initialStakeRemaining = 0;
    winningsBalance -= fromWinnings;
  }

  // Create updated participant
  const updatedParticipant: Participant = {
    ...participant,
    initialStakeRemaining,
    winningsBalance,
    totalAvailable: calculateTotalAvailable(initialStakeRemaining, winningsBalance),
  };

  return updatedParticipant;
}

/**
 * Add winnings to participant wallet
 * Adds to winningsBalance and updates totalAvailable
 * 
 * @param participant - Participant to add winnings to
 * @param amount - Amount to add
 * @returns Updated participant with new balances
 */
export function addWinnings(
  participant: Participant,
  amount: number
): Participant {
  const newWinningsBalance = participant.winningsBalance + amount;

  return {
    ...participant,
    winningsBalance: newWinningsBalance,
    totalAvailable: calculateTotalAvailable(
      participant.initialStakeRemaining,
      newWinningsBalance
    ),
  };
}

/**
 * Check if participant has sufficient funds for a bet
 * 
 * @param participant - Participant to check
 * @param amount - Bet amount to check
 * @returns True if sufficient funds, false otherwise
 */
export function hasSufficientFunds(
  participant: Participant,
  amount: number
): boolean {
  const totalAvailable = calculateTotalAvailable(
    participant.initialStakeRemaining,
    participant.winningsBalance
  );
  return totalAvailable >= amount;
}

/**
 * Create a new participant with initial $1000 stake
 * 
 * @param id - Participant ID
 * @param name - Participant name
 * @param eventId - Event ID
 * @returns New participant with $1000 initial stake
 */
export function createParticipant(
  id: string,
  name: string,
  eventId: string
): Participant {
  const initialStake = 1000;
  const now = Date.now();

  return {
    id,
    name,
    eventId,
    initialStake,
    initialStakeRemaining: initialStake,
    winningsBalance: 0,
    totalAvailable: initialStake,
    bets: [],
    joinedAt: now,
  };
}

/**
 * Convert American odds to decimal odds
 */
function americanToDecimal(americanOdds: number): number {
  if (americanOdds > 0) {
    return (americanOdds / 100) + 1;
  } else {
    return (100 / Math.abs(americanOdds)) + 1;
  }
}

/**
 * Get wallet summary for display
 * Returns formatted strings for UI display
 */
export function getWalletSummary(participant: Participant) {
  const bets = participant.bets || [];
  
  // Calculate current bets at risk (pending bets)
  const pendingBets = bets.filter(bet => bet.status === 'pending');
  const currentBetsAmount = pendingBets.reduce((sum, bet) => sum + bet.amount, 0);
  
  // Calculate total potential profit (sum of individual bet profits)
  const totalPotentialProfit = pendingBets.reduce((sum, bet) => {
    const decimalOdds = americanToDecimal(bet.odds);
    const profit = bet.amount * (decimalOdds - 1);
    return sum + profit;
  }, 0);
  
  // Calculate potential winnings (same as total potential profit)
  const potentialWinnings = totalPotentialProfit;
  
  // Back into overall odds from total risk and total profit
  let overallOdds = 0;
  if (currentBetsAmount > 0 && totalPotentialProfit > 0) {
    const profitRatio = totalPotentialProfit / currentBetsAmount;
    if (profitRatio >= 1) {
      // Positive odds
      overallOdds = Math.round(profitRatio * 100);
    } else {
      // Negative odds
      overallOdds = Math.round(-100 / profitRatio);
    }
  }
  
  // Calculate total bets lost
  const lostBets = bets.filter(bet => bet.status === 'lost');
  const totalLost = lostBets.reduce((sum, bet) => sum + bet.amount, 0);
  
  // Calculate total bets won (profit from winning bets)
  const wonBets = bets.filter(bet => bet.status === 'won');
  const totalWon = wonBets.reduce((sum, bet) => {
    const decimalOdds = americanToDecimal(bet.odds);
    const profit = bet.amount * (decimalOdds - 1);
    return sum + profit;
  }, 0);

  return {
    initialStake: participant.initialStake,
    initialStakeRemaining: participant.initialStakeRemaining,
    winningsBalance: participant.winningsBalance,
    totalAvailable: participant.totalAvailable,
    hasInitialStakeRemaining: participant.initialStakeRemaining > 0,
    hasWinnings: participant.winningsBalance > 0,
    // For leaderboard: only winnings count
    leaderboardValue: participant.winningsBalance,
    // New betting stats
    currentBetsAmount,
    overallOdds,
    potentialWinnings,
    totalLost,
    totalWon,
    hasPendingBets: pendingBets.length > 0,
  };
}






